import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Clock,
  Settings,
  ArrowLeft,
  PanelLeftClose,
  PanelLeftOpen,
  Bell,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';
import { DashboardHome } from '../dashboardpages/DashboardHome';
import { DashboardProducts } from '../dashboardpages/DashboardProducts';
import { DashboardOrders } from '../dashboardpages/DashboardOrders';
import { DashboardOrdersInProgress } from '../dashboardpages/DashboardOrdersInProgress';
import { DashboardSettings } from '../dashboardpages/DashboardSettings';
import { DashboardNotifications } from '../dashboardpages/DashboardNotifications';
import { DashboardProductEdit } from '../dashboardpages/DashboardProductEdit';
import { DashboardThemeProvider, useDashboardTheme } from '../context/DashboardThemeContext';

const ADMIN_ID = '92040e94-abd9-4dd2-971f-a1be769d4f11';

type Section = 'dashboard' | 'products' | 'orders' | 'in-progress' | 'notifications' | 'settings';

const nav: { id: Section; path: string; labelKey: string; icon: typeof LayoutDashboard }[] = [
  { id: 'dashboard', path: '', labelKey: 'business.dashboard', icon: LayoutDashboard },
  { id: 'products', path: 'products', labelKey: 'business.products', icon: Package },
  { id: 'orders', path: 'orders', labelKey: 'business.orders', icon: ShoppingBag },
  { id: 'in-progress', path: 'in-progress', labelKey: 'business.orders_in_progress', icon: Clock },
  { id: 'notifications', path: 'notifications', labelKey: 'business.notifications', icon: Bell },
  { id: 'settings', path: 'settings', labelKey: 'business.settings', icon: Settings },
];

export default function BusinessDashboardPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { section, productId } = useParams<{ section?: string; productId?: string }>();
  const [profile, setProfile] = useState<{ role?: string } | null>(null);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const currentSection: Section =
    productId ? 'products' : section && nav.some((n) => n.path === section) ? (section as Section) : 'dashboard';

  useEffect(() => {
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }
      setUser(session.user);
      const { data: profileData } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .single();
      setProfile(profileData || null);
      setLoading(false);
    };
    load();
  }, [navigate]);

  const isAdmin = profile?.role === 'admin' || user?.id === ADMIN_ID;

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex flex-col items-center justify-center px-6">
        <p className="font-['Rubik'] text-white/70 text-center mb-6">{t('business.access_denied')}</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-['Rubik'] text-sm text-white underline hover:no-underline"
        >
          <ArrowLeft className="w-4 h-4" /> Retour à l&apos;accueil
        </Link>
      </div>
    );
  }

  const base = '/espace-business';

  return (
    <DashboardThemeProvider isAdmin={isAdmin}>
      <DashboardLayout base={base} currentSection={currentSection} productId={productId ?? null} t={t} />
    </DashboardThemeProvider>
  );
}

function DashboardLayout({
  base,
  currentSection,
  productId,
  t,
}: {
  base: string;
  currentSection: Section;
  productId: string | null;
  t: (key: string) => string;
}) {
  const { theme } = useDashboardTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isLight = theme === 'light';
  const sidebarW = sidebarCollapsed ? 80 : 256;

  return (
    <div
      className={`flex min-h-screen ${isLight ? 'bg-neutral-100 text-neutral-900' : 'bg-neutral-950 text-white'}`}
      data-dashboard-theme={theme}
    >
      {/* Sidebar : au-dessus du header global (z-[110] > Navbar z-[100]) pour mobile et desktop */}
      <aside
        className={`fixed inset-y-0 left-0 z-[110] flex flex-col border-r pb-6 transition-[width] duration-200 ${
          isLight ? 'border-neutral-200 bg-neutral-50 text-neutral-900' : 'border-white/10 bg-neutral-950'
        }`}
        style={{ width: sidebarW }}
      >
        {/* Logo + bouton : même hauteur que le header, aligné en haut */}
        <div
          className={`flex items-center shrink-0 border-b h-16 md:h-20 ${isLight ? 'border-neutral-200' : 'border-white/10'} px-3 gap-2`}
        >
          <Link
            to="/"
            className={`flex shrink-0 items-center gap-2 min-w-0 ${sidebarCollapsed ? 'flex-1 justify-center' : ''}`}
            aria-label="Maison Adji"
          >
            <img
              src={isLight ? '/assets/images/maisonadjiblack.png' : '/assets/images/maisonadjiwhite.png'}
              alt=""
              className="h-8 w-auto object-contain shrink-0"
            />
            {!sidebarCollapsed && (
              <span className="font-['Sora'] text-sm font-medium truncate">Maison Adji</span>
            )}
          </Link>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSidebarCollapsed((c) => !c);
            }}
            className={`shrink-0 cursor-pointer rounded-lg p-2 min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors ${
              isLight
                ? 'text-neutral-600 hover:bg-neutral-200 hover:text-neutral-900'
                : 'text-white/60 hover:bg-white/10 hover:text-white'
            } ${!sidebarCollapsed ? 'ml-auto' : ''}`}
            aria-label={sidebarCollapsed ? 'Déployer le menu' : 'Réduire le menu'}
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen className="h-5 w-5" aria-hidden />
            ) : (
              <PanelLeftClose className="h-5 w-5" aria-hidden />
            )}
          </button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3">
          {nav.map(({ id, path, labelKey, icon: Icon }) => (
            <NavLink
              key={id}
              to={path ? `${base}/${path}` : base}
              end={path === ''}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl py-3 font-['Rubik'] text-sm transition-colors ${
                  sidebarCollapsed ? 'justify-center px-0' : 'px-3'
                } ${
                  isLight
                    ? isActive
                      ? 'bg-neutral-200 text-neutral-900'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                    : isActive
                      ? 'bg-white/10 text-white'
                      : 'text-white/60 hover:bg-white/5 hover:text-white'
                }`
              }
              title={sidebarCollapsed ? t(labelKey) : undefined}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {!sidebarCollapsed && <span className="truncate">{t(labelKey)}</span>}
            </NavLink>
          ))}
        </nav>
        <div className={`border-t px-3 pt-4 pb-4 ${sidebarCollapsed ? 'flex justify-center' : ''} ${isLight ? 'border-neutral-200' : 'border-white/10'}`}>
          <Link
            to="/"
            className={`flex items-center gap-2 font-['Rubik'] text-xs hover:opacity-80 ${
              sidebarCollapsed ? 'justify-center' : ''
            } ${isLight ? 'text-neutral-500 hover:text-neutral-900' : 'text-white/50 hover:text-white'}`}
            title="Retour au site"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            {!sidebarCollapsed && <span>Retour au site</span>}
          </Link>
        </div>
      </aside>

      {/* Zone de contenu : décalée à droite (pl-64), marges réduites pour plus d’espace */}
      <div
        className={`min-h-screen flex-1 pt-16 md:pt-20 pb-8 transition-[padding-left] duration-200 ${isLight ? 'bg-neutral-100' : ''}`}
        style={{ paddingLeft: sidebarW }}
      >
        <main className="w-full px-4 sm:px-5 md:px-6 lg:px-8">
          {productId ? (
            <DashboardProductEdit productId={productId} />
          ) : (
            <>
              {currentSection === 'dashboard' && <DashboardHome />}
              {currentSection === 'products' && <DashboardProducts />}
              {currentSection === 'orders' && <DashboardOrders />}
              {currentSection === 'in-progress' && <DashboardOrdersInProgress />}
              {currentSection === 'notifications' && <DashboardNotifications />}
              {currentSection === 'settings' && <DashboardSettings />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
